/**
 * awp cart-state — a single shared source of truth for the cart on the storefront.
 *
 * WHY THIS EXISTS
 * The theme has several independent cart consumers (native product forms, the AWP
 * product form, the cart drawer) and several app-injected ones (RestockRocket,
 * Klaviyo) that each fetched `/cart.js` on their own — on load and on every cart
 * change. A single page load could fire `/cart.js` ~6 times, and two reads could
 * land in the same millisecond (a race). This broker gives every caller ONE
 * cached cart and ONE in-flight request, and a pub/sub channel so consumers stop
 * fetching independently.
 *
 * It is a classic (non-module) IIFE attached to `window.Awp.CartState` so that
 * BOTH the theme's ES modules and any app/theme-app-embed script can share the
 * same instance regardless of load order. It touches no framework and has no
 * dependencies, so it is safe to load first in <head>.
 *
 * PUBLIC API — window.Awp.CartState
 *   get(opts?)      → Promise<cart>  Cached cart if fresh (< COALESCE_MS old),
 *                                    otherwise a single coalesced GET /cart.js.
 *                                    Concurrent callers share one in-flight promise.
 *                                    { force: true } bypasses the freshness window.
 *   set(cart)       → void           Adopt a cart object a mutation already returned
 *                                    (e.g. the JSON from /cart/change.js) as the new
 *                                    truth WITHOUT a refetch, then publish.
 *   invalidate()    → Promise<cart>  Drop the cache and refetch once (coalesced).
 *                                    Call after a mutation whose full cart you don't
 *                                    have in hand.
 *   subscribe(fn)   → unsubscribe    fn(cart) runs on every publish; if a cart is
 *                                    already cached, fn is called immediately once.
 *   current         → cart | null    Last known cart, synchronously (may be null).
 *
 * EVENTS
 *   Dispatches `cart:updated` on `document` with { detail: { cart, source } } on
 *   every publish, for consumers that prefer events over subscribe().
 */
(function () {
  'use strict';

  if (window.Awp && window.Awp.CartState) return; // already installed

  // No two `/cart.js` reads inside this window; a cached cart younger than this
  // is returned without a network call. 500ms comfortably covers the burst of
  // consumers that all wake on the same cart:update tick.
  var COALESCE_MS = 500;

  var CART_URL = (window.Theme && window.Theme.routes && window.Theme.routes.cart_url) || '/cart.js';
  // Theme.routes.cart_url is the localized /cart path; append .js for the JSON view.
  if (CART_URL.indexOf('.js') === -1) CART_URL = CART_URL.replace(/\/?$/, '') + '.js';

  /** @type {any} */ var cache = null;
  var cacheAt = 0;
  /** @type {Promise<any> | null} */ var inflight = null;
  /** @type {Set<Function>} */ var subs = new Set();

  function stamp() {
    // Date.now via a function so we never read it at module-eval time.
    return Date.now();
  }

  function isFresh() {
    return cache !== null && stamp() - cacheAt < COALESCE_MS;
  }

  /**
   * Sets the cache and notifies every subscriber + fires the DOM event.
   * @param {any} cart
   * @param {string} source
   */
  function publish(cart, source) {
    cache = cart;
    cacheAt = stamp();
    subs.forEach(function (fn) {
      try {
        fn(cart);
      } catch (err) {
        console.error('[awp-cart-state] subscriber threw', err);
      }
    });
    document.dispatchEvent(
      new CustomEvent('cart:updated', { detail: { cart: cart, source: source || 'cart-state' } })
    );
  }

  /**
   * Returns the cart, coalescing concurrent reads into one request.
   * @param {{ force?: boolean }} [opts]
   * @returns {Promise<any>}
   */
  function get(opts) {
    var force = !!(opts && opts.force);
    if (!force && isFresh()) return Promise.resolve(cache);
    if (inflight) return inflight;

    inflight = fetch(CART_URL, { headers: { 'X-Requested-With': 'fetch' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        publish(cart, 'fetch');
        return cart;
      })
      .catch(function (err) {
        console.error('[awp-cart-state] /cart.js fetch failed', err);
        return cache; // fall back to last known cart rather than rejecting callers
      })
      .then(function (cart) {
        inflight = null;
        return cart;
      });

    return inflight;
  }

  /**
   * Adopt a cart object a mutation already returned — no refetch.
   * @param {any} cart
   */
  function set(cart) {
    if (cart && typeof cart.item_count === 'number') publish(cart, 'mutation');
  }

  /**
   * Drop the cache and refetch once.
   * @returns {Promise<any>}
   */
  function invalidate() {
    cacheAt = 0;
    return get({ force: true });
  }

  /**
   * Subscribe to cart updates. Fires immediately with the cached cart if present.
   * @param {(cart: any) => void} fn
   * @returns {() => void} unsubscribe
   */
  function subscribe(fn) {
    subs.add(fn);
    if (cache !== null) {
      try {
        fn(cache);
      } catch (err) {
        console.error('[awp-cart-state] subscriber threw on immediate replay', err);
      }
    }
    return function () {
      subs.delete(fn);
    };
  }

  window.Awp = window.Awp || {};
  window.Awp.CartState = {
    get: get,
    set: set,
    invalidate: invalidate,
    subscribe: subscribe,
    get current() {
      return cache;
    },
  };

  // When the drawer / any consumer dispatches the theme's cart-change event, keep
  // our cache honest by adopting the payload if present, else invalidating. We
  // listen passively so we never *initiate* a read on load — only react to real
  // mutations other code already performed.
  document.addEventListener('cart:update', function (/** @type {any} */ e) {
    var data = e && e.detail && e.detail.data;
    // The theme's CartUpdateEvent carries item metadata, not always the full cart.
    // If a full cart object is present, adopt it; otherwise mark stale so the next
    // get() refetches. We do NOT eagerly refetch here to avoid a read per event.
    if (data && typeof data.item_count === 'number' && Array.isArray(data.items)) {
      set(data);
    } else {
      cacheAt = 0; // stale — next get() will fetch fresh
    }
  });
})();
